/**
 * Request a withdrawal.
 *
 * Unlike a deposit this *does* move money the moment it is submitted: the funds
 * are locked (`available → locked`) inside the same transaction that creates
 * the row, before any human reviews it (§3). So the confirmation says the money
 * is on hold, because it truthfully is — a player who saw their balance drop
 * and no explanation would reasonably think it had been sent already.
 *
 * The amount offered is `withdrawable`, never `available`. Offering the larger
 * number and then refusing it is the interaction the §3 gate is most likely to
 * be blamed for.
 */

import { useEffect, useState } from 'react';
import { api, intentKey } from '../api/client.ts';
import { errorMessage } from '../api/errors.ts';
import type { BalanceView, BankMethod } from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import { etb, etbBare, parseAmount, amountGreaterThan, isZero } from '../format.ts';
import { haptics } from '../telegram.ts';
import { cleanPhone, checkPhone } from '../validation.ts';

interface WithdrawProps {
  onDone(): void;
  onBack(): void;
}

/**
 * TeleBirr only, by operator decision (2026-09-10). CBE withdrawals were
 * offered but never wired to anything that could pay one: the dispatcher
 * drives the TeleBirr app and nothing else, so a CBE request could only ever
 * wait for a human. Removing the choice is honest about that, and it also
 * lets the destination field mean one thing — a phone number — which is what
 * makes checking its shape possible.
 *
 * Kept as a named constant rather than inlined: the API still takes a
 * `method`, and this is the line to change when there is a second one again.
 */
const WITHDRAW_METHOD: BankMethod = 'telebirr';

export function Withdraw({ onDone, onBack }: WithdrawProps) {
  const t = useT();
  const [balance, setBalance] = useState<BalanceView | null>(null);
  const method = WITHDRAW_METHOD;
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // The withdrawable figure specifically, not the session balance — it is the
    // only number this screen may offer.
    void api
      .withdrawableBalance()
      .then(setBalance)
      .catch((err) => setSubmitError(errorMessage(err, t)));
  }, [t]);

  async function submit(): Promise<void> {
    if (!balance) return;
    setFieldError(null);
    setSubmitError(null);

    const parsed = parseAmount(amount);
    if (!parsed.ok) {
      setFieldError(parsed.reason === 'empty' ? t.errorAmountRequired : t.errorAmountInvalid);
      return;
    }
    if (amountGreaterThan(parsed.santim, balance.withdrawable)) {
      setFieldError(t.errorMoreThanWithdrawable);
      return;
    }

    // Cleaned before checked, and the cleaned value is what is submitted, so
    // the destination stored is the canonical one the dispatcher will type.
    const dest = cleanPhone(destination);
    const destProblem = checkPhone(dest);
    if (destProblem) {
      setFieldError(destProblem === 'empty' ? t.errorDestinationRequired : t.errorTelebirrNumber);
      return;
    }

    setSubmitting(true);
    try {
      await api.requestWithdrawal(
        { method, amount: parsed.santim, destinationDetail: dest },
        intentKey('withdraw'),
      );
      haptics.win();
      setDone(true);
    } catch (err) {
      setSubmitError(errorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="screen screen--centred">
        <div className="outcome__mark outcome__mark--win">✓</div>
        <h1 className="outcome__title">{t.withdrawRequested}</h1>
        <p className="outcome__body">{t.withdrawRequestedBody}</p>
        <button type="button" className="button button--primary button--wide" onClick={onDone}>
          {t.done}
        </button>
      </div>
    );
  }

  const nothingToWithdraw = balance !== null && isZero(balance.withdrawable);

  return (
    <div className="screen">
      <header className="formhead">
        <button type="button" className="roundhead__back" onClick={onBack} aria-label="Back">
          ‹
        </button>
        <h1 className="formhead__title">{t.withdrawTitle}</h1>
      </header>

      {balance === null && <p className="activity__empty">{t.connecting}</p>}

      {nothingToWithdraw && (
        <div className="wagering">
          <p className="wagering__headline">{t.withdrawNothing}</p>
        </div>
      )}

      {balance && !nothingToWithdraw && (
        <>
          <p className="withdrawable">{t.withdrawAvailable(etb(balance.withdrawable))}</p>

          {/* Migration 066: bonus money (welcome bonus, referral reward) is
              playable but never withdrawable — without this line the smaller
              withdrawable figure would read as an app bug rather than a rule. */}
          {!isZero(balance.bonusLocked) && (
            <div className="wagering">
              <p className="wagering__headline">{t.bonusLockedHint}</p>
            </div>
          )}

          <label className="field">
            <span className="field__label">{t.withdrawAmount}</span>
            <div className="field__withaction">
              <input
                className="field__input"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {/* Fills the exact withdrawable figure, so "all of it" does not
                  require the player to retype a number they can see. */}
              <button
                type="button"
                className="field__action"
                onClick={() => setAmount(etbBare(balance.withdrawable))}
              >
                {t.withdrawMax}
              </button>
            </div>
          </label>

          <label className="field">
            <span className="field__label">{t.withdrawDestination}</span>
            <input
              className="field__input"
              inputMode="tel"
              autoCorrect="off"
              spellCheck={false}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
            <span className="field__hint">{t.withdrawDestinationHint}</span>
          </label>

          {fieldError && <div className="banner banner--error">{fieldError}</div>}
          {submitError && <div className="banner banner--error">{submitError}</div>}

          <button
            type="button"
            className="button button--primary button--wide"
            onClick={() => void submit()}
            disabled={submitting}
          >
            {submitting ? t.withdrawSubmitting : t.withdrawSubmit}
          </button>
        </>
      )}
    </div>
  );
}
